/**
 * Unified Permission Service
 * Provides a consistent interface for managing permissions across all resource types
 * Wraps existing services and adapters without modifying backend
 */
import FolderPermissionAdapter from './permissions/adapters/FolderPermissionAdapter';
import MeetingPermissionAdapter from './permissions/adapters/MeetingPermissionAdapter';
import OrgPermissionAdapter from './permissions/adapters/OrgPermissionAdapter';
import { RESOURCE_TYPES } from './permissions/constants';

// Map resource types to their adapters
const adapters = {
  [RESOURCE_TYPES.FOLDER]: FolderPermissionAdapter,
  [RESOURCE_TYPES.MEETING]: MeetingPermissionAdapter,
  [RESOURCE_TYPES.ORGANIZATION]: OrgPermissionAdapter,
};

/**
 * Get the adapter for a resource type
 * @param {string} resourceType - Type of resource (folder, meeting, etc.)
 * @returns {Object} Adapter instance
 * @throws {Error} If resource type is not supported
 */
const getAdapter = (resourceType) => {
  const adapter = adapters[resourceType];
  if (!adapter) {
    throw new Error(
      `Unsupported resource type: ${resourceType}. Supported types: ${Object.keys(adapters).join(', ')}`
    );
  }
  return adapter;
};

const PermissionService = {
  /**
   * Get all users with access to a resource
   * @param {string} resourceId - Resource ID
   * @param {string} resourceType - Type of resource (folder, meeting, etc.)
   * @param {Object} options - Additional options (adapter-specific)
   * @returns {Promise<Array>} Array of users with their roles
   */
  async getUsers(resourceId, resourceType, options = {}) {
    const adapter = getAdapter(resourceType);
    return adapter.getUsers(resourceId, options);
  },

  /**
   * Assign role to user(s) in a resource
   * @param {string} resourceId - Resource ID
   * @param {string} resourceType - Type of resource
   * @param {string|Array<string>} emails - Email(s) of user(s) to assign
   * @param {string} role - Role to assign
   * @param {Object} options - Additional options (message, etc.)
   * @returns {Promise<Object>} Response data
   */
  async assignRole(resourceId, resourceType, emails, role, options = {}) {
    const adapter = getAdapter(resourceType);
    return adapter.assignRole(resourceId, emails, role, options);
  },

  /**
   * Update user's role in a resource
   * @param {string} resourceId - Resource ID
   * @param {string} resourceType - Type of resource
   * @param {string} userId - User ID
   * @param {string} role - New role
   * @returns {Promise<Object>} Updated data
   */
  async updateRole(resourceId, resourceType, userId, role) {
    const adapter = getAdapter(resourceType);
    return adapter.updateRole(resourceId, userId, role);
  },

  /**
   * Remove user access from a resource
   * @param {string} resourceId - Resource ID
   * @param {string} resourceType - Type of resource
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Response data
   */
  async removeUser(resourceId, resourceType, userId) {
    const adapter = getAdapter(resourceType);
    return adapter.removeUser(resourceId, userId);
  },

  /**
   * Reactivate a user (organization scope)
   * @param {string} resourceId - Organization ID
   * @param {string} resourceType - Should be organization
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Response data
   */
  async reactivateUser(resourceId, resourceType, userId) {
    const adapter = getAdapter(resourceType);
    if (!adapter.reactivateUser) {
      throw new Error(`Reactivate not supported for resource type: ${resourceType}`);
    }
    return adapter.reactivateUser(resourceId, userId);
  },

  /**
   * Resend invitation email to a user (organization scope)
   * @param {string} resourceId - Organization ID
   * @param {string} resourceType - Should be organization
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Response data
   */
  async resendInvitation(resourceId, resourceType, userId) {
    const adapter = getAdapter(resourceType);
    if (!adapter.resendInvitation) {
      throw new Error(
        `Resend invitation not supported for resource type: ${resourceType}`
      );
    }
    return adapter.resendInvitation(resourceId, userId);
  },

  /**
   * Check if a resource type is supported
   * @param {string} resourceType - Type of resource
   * @returns {boolean} True if supported
   */
  isResourceTypeSupported(resourceType) {
    return resourceType in adapters;
  },

  /**
   * Get supported resource types
   * @returns {Array<string>} Array of supported resource types
   */
  getSupportedResourceTypes() {
    return Object.keys(adapters);
  },
};

export default PermissionService;

